import { MediaObject } from 'types'
import validators from './base'
import { ScheduledPostSettings } from 'api/clients/ScheduledPostsClient'

// Source: https://help.hootsuite.com/hc/en-us/articles/1260804250050-Video-file-requirements#video-file-requirements-0-0
// Source: https://docs.ayrshare.com/additional-info/image-and-video-requirements

const instagramContentValidator = (content: string): string[] => {
  const rules = {
    maxCharacters: 2200,
    maxHashtags: 30,
    maxMentions: 20,
  }

  let errors = validators.validateTextLength(content, rules.maxCharacters, false)
  if (rules.maxHashtags) errors = [...errors, ...validators.validateHashtags(content, rules.maxHashtags)]
  if (rules.maxMentions) errors = [...errors, ...validators.validateMentions(content, rules.maxMentions)]
  return errors
}

// Instagram Story: no caption, hashtag, or mention validation
const instagramStoryContentValidator = (_content: string): string[] => []

const instagramFileValidator = (files: MediaObject[]): string[] => {
  const rules = {
    minImages: 1,
    maxImages: 10,
  }

  return validators.validateFileCount(files, rules.minImages, rules.maxImages)
}

// Instagram Reels specific file size validation (300MB limit)
const instagramReelsFileSizeValidator = (files: MediaObject[]): string[] => {
  const maxSize = 300 * 1024 * 1024 // 300MB in bytes
  const videos = files.filter((file) => file.mime_type?.startsWith('video/'))
  const errors: string[] = []

  videos.forEach((video) => {
    if (video.size && video.size > maxSize) {
      const sizeInMB = Math.round(video.size / (1024 * 1024))
      errors.push(`File size must be ≤300MB, found: ${sizeInMB}MB`)
    }
  })

  return errors
}

// Instagram Reels video width validation (≤1920px)
const instagramReelsWidthValidator = (files: MediaObject[]): string[] => {
  const maxWidth = 1920
  const videos = files.filter((file) => file.mime_type?.startsWith('video/'))
  const errors: string[] = []

  videos.forEach((video) => {
    if (video.width && video.width > maxWidth) {
      errors.push(`Width must be ≤1920px, found: ${video.width}px`)
    }
  })

  return errors
}

// Instagram Reels aspect ratio validation with 9:16 recommendation
const instagramReelsAspectRatioValidator = (files: MediaObject[]): string[] => {
  const minAspectRatio = 0.01
  const maxAspectRatio = 10.0
  const recommendedMinRatio = 0.5 // Close to 9:16 (0.5625)
  const recommendedMaxRatio = 0.6
  const videos = files.filter((file) => file.mime_type?.startsWith('video/'))
  const errors: string[] = []
  const warnings: string[] = []

  videos.forEach((video) => {
    if (video.aspect_ratio) {
      const ratio = video.aspect_ratio

      if (ratio < minAspectRatio || ratio > maxAspectRatio) {
        errors.push(`Aspect ratio must be between 0.01:1 and 10:1, found: ${ratio.toFixed(3)}:1`)
      } else {
        // Check if it's close to recommended 9:16
        if (ratio < recommendedMinRatio || ratio > recommendedMaxRatio) {
          warnings.push(`Consider 9:16 aspect ratio to avoid cropping/blank space (current: ${ratio.toFixed(3)}:1)`)
        }
      }
    }
  })

  return [...errors, ...warnings]
}

// Instagram Reels frame rate validation (23-60fps)
const instagramReelsFrameRateValidator = (files: MediaObject[]): string[] => {
  const minFps = 23
  const maxFps = 60
  const videos = files.filter((file) => file.mime_type?.startsWith('video/'))
  const errors: string[] = []

  videos.forEach((video) => {
    if (video.frame_rate) {
      if (video.frame_rate < minFps || video.frame_rate > maxFps) {
        errors.push(`Frame rate must be 23-60fps, found: ${video.frame_rate.toFixed(2)}fps`)
      }
    }
  })

  return errors
}

// Instagram Reels duration validation (3s to 15min)
const instagramReelsDurationValidator = (files: MediaObject[]): string[] => {
  const minDuration = 3 // 3 seconds
  const maxDuration = 900 // 15 minutes (900 seconds)
  const videos = files.filter((file) => file.mime_type?.startsWith('video/'))
  const errors: string[] = []

  videos.forEach((video) => {
    if (video.duration) {
      if (video.duration < minDuration || video.duration > maxDuration) {
        const durationMin = (video.duration / 60).toFixed(1)
        errors.push(`Duration must be 3 seconds to 15 minutes, found: ${video.duration}s (${durationMin}min)`)
      }
    }
  })

  return errors
}

// Instagram Reels format validation (MOV/MP4)
const instagramReelsFormatValidator = (files: MediaObject[]): string[] => {
  const supportedFormats = ['video/mp4', 'video/quicktime', 'video/x-msvideo']
  const videos = files.filter((file) => file.mime_type?.startsWith('video/'))
  const errors: string[] = []

  videos.forEach((video) => {
    if (video.mime_type && !supportedFormats.includes(video.mime_type)) {
      errors.push(`Container must be MOV or MP4, found: ${video.mime_type}`)
    }
  })

  return errors
}

// Instagram Reels video count validation (exactly 1 video)
const instagramReelsVideoCountValidator = (files: MediaObject[]): string[] => {
  const videos = files.filter((file) => file.mime_type?.startsWith('video/'))
  const errors: string[] = []

  if (videos.length !== 1) {
    errors.push('Instagram Reels must contain exactly 1 video')
  }

  return errors
}

// Instagram Reels file count validation (exactly 1 file)
const instagramReelsFileCountValidator = (files: MediaObject[]): string[] => {
  const errors: string[] = []

  if (files.length !== 1) {
    errors.push('Instagram Reels must contain exactly 1 file')
  }

  return errors
}

const instagramReelsValidators = {
  validateContent: instagramContentValidator,
  validateFiles: instagramReelsFileCountValidator,
  validateVideosCount: instagramReelsVideoCountValidator,
  validateVideosSize: instagramReelsFileSizeValidator,
  validateImagesSize: () => [], // Instagram Reels don't support images
  validateVideosDuration: instagramReelsDurationValidator,
  validateVideosAspectRatio: instagramReelsAspectRatioValidator,
  validateVideosFormat: instagramReelsFormatValidator,
  validateVideosWidth: instagramReelsWidthValidator,
  validateVideosFrameRate: instagramReelsFrameRateValidator,
  validatePostSettings: (_settings?: ScheduledPostSettings): string[] => [],
}

const instagramVideosSizeValidator = (files: MediaObject[]): string[] => {
  const rules = {
    maxSize: 300 * 1024 * 1024, // 300MB
  }

  return validators.validateVideosSize(files, rules.maxSize, 'MB')
}

const instagramImageSizeValidator = (files: MediaObject[]): string[] => {
  const rules = {
    maxSize: 8 * 1024 * 1024, // 8MB
  }

  return validators.validateImagesSize(files, rules.maxSize, 'MB')
}

const instagramDurationValidator = (files: MediaObject[]): string[] => {
  const rules = {
    minDuration: 3,
    maxDuration: 900, // 15 minutes
  }

  return validators.validateVideosDuration(files, rules.minDuration, rules.maxDuration)
}

const instagramVideoAspectRatioValidator = (files: MediaObject[]): string[] => {
  const rules = {
    minAspectRatio: 0.01,
    maxAspectRatio: 10.0,
  }

  const labels = {
    minAspectRatio: '0.01:1',
    maxAspectRatio: '10:1',
  }

  return validators.validateVideosAspectRatio(
    files,
    rules.minAspectRatio,
    rules.maxAspectRatio,
    labels.minAspectRatio,
    labels.maxAspectRatio,
  )
}

const instagramFormatValidator = (files: MediaObject[]): string[] => {
  const formats = ['video/mp4', 'video/quicktime']

  return validators.validateVideosFormat(files, formats, 'Instagram')
}

const instagramVideosCountValidator = (_files: MediaObject[]): string[] => {
  return []
}

const instagramPostSettingsValidator = (_settings?: ScheduledPostSettings): string[] => {
  return []
}

const instagramValidators = {
  validateContent: instagramContentValidator,
  validateFiles: instagramFileValidator,
  validateVideosSize: instagramVideosSizeValidator,
  validateVideosDuration: instagramDurationValidator,
  validateImagesSize: instagramImageSizeValidator,
  validateVideosAspectRatio: instagramVideoAspectRatioValidator,
  validateVideosFormat: instagramFormatValidator,
  validateVideosCount: instagramVideosCountValidator,
  validatePostSettings: instagramPostSettingsValidator,
  validateVideosWidth: instagramReelsWidthValidator,
}

const instagramStoryValidators = {
  ...instagramValidators,
  validateContent: instagramStoryContentValidator,
  validateVideosFrameRate: (): string[] => [],
}

export { instagramValidators, instagramReelsValidators, instagramStoryValidators }
